import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { generateSecureId, encryptForStorage, decryptFromStorage } from '@/lib/utils';
import { setSentryWidgetSession } from '@/lib/sentry';
import type {
  RoomType,
  RenovationSelections,
  ProjectTypeSelections,
  WidgetContact,
  WidgetGeminiAnalysis,
  WidgetSessionStatus,
  PricingConfig,
  WidgetConfig,
  WidgetAnalyticsEvent,
  BrandingConfig,
  SherwinWilliamsColor,
} from '@/types/firestore';
import { getRoomTypes, getProjectTypes } from './config/roomOptions';
import { safeJsonParse, fetchWithRetry, fetchWithTimeout } from './utils/safeApi';
// Quote calculation has been removed - pricing is no longer shown in widget

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Widget step definitions
export type WidgetStep =
  | 'room_select'
  | 'image_upload'
  | 'selections'
  | 'contact'
  | 'preview_teaser'
  | 'phone_gate'
  | 'verify_code'
  | 'project_details'
  | 'results';

const STEP_ORDER: WidgetStep[] = [
  'room_select',
  'image_upload',
  'preview_teaser',
  'selections',
  'contact',
  'phone_gate',
  'verify_code',
  'project_details',
  'results',
];

// Generation status tracking (not persisted - transient per session)
export type GenerationStatus = 'idle' | 'uploading' | 'generating' | 'complete' | 'error';

// Session state stored in localStorage
interface WidgetSessionState {
  sessionId: string | null;
  tenantId: string;
  currentStep: WidgetStep;
  roomType: RoomType | null;
  projectTypes: string[]; // Multi-select project types (e.g., ['deck', 'pergola'])
  uploadedImages: string[]; // Firebase Storage paths
  imageFiles: File[]; // Local files before upload
  selections: RenovationSelections; // Flat selections for areas without project types
  projectTypeSelections: ProjectTypeSelections; // Per-project-type selections
  contact: WidgetContact;
  stylePreference: string | null;
  additionalDetails: string | null; // User's additional project details

  // After n8n response
  geminiAnalysis: WidgetGeminiAnalysis | null;
  generatedImage: string | null;
  blurredPreview: string | null;
  // quoteRange has been removed - pricing is no longer shown in widget

  // Phone verification
  phoneNumber: string | null;
  revisionCount: number;

  // Progressive lead creation
  leadId: string | null;
  hasCredits: boolean | null;

  // Metadata
  startedAt: string;
  lastUpdated: string;
}

interface WidgetContextValue {
  // State
  state: WidgetSessionState;
  tenantConfig: {
    company_name: string;
    branding: BrandingConfig;
    widget: WidgetConfig;
    pricing: PricingConfig;
    custom_colors?: SherwinWilliamsColor[];
  } | null;
  isLoading: boolean;
  error: string | null;

  // Navigation
  currentStep: WidgetStep;
  canGoNext: boolean;
  canGoPrev: boolean;
  goToStep: (step: WidgetStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Actions
  setRoomType: (type: RoomType) => void;
  setProjectTypes: (types: string[]) => void;
  /** @deprecated Use setProjectTypes instead */
  setConversionType: (type: string | undefined) => void;
  addImageFile: (file: File) => void;
  removeImageFile: (index: number) => void;
  setUploadedImages: (paths: string[]) => void;
  updateSelections: (selections: Partial<RenovationSelections>) => void;
  updateProjectTypeSelections: (projectTypeId: string, selections: Partial<RenovationSelections>) => void;
  setStylePreference: (style: string) => void;
  setAdditionalDetails: (details: string | undefined) => void;
  updateContact: (contact: Partial<WidgetContact>) => void;
  setPhoneNumber: (phone: string) => void;

  // n8n integration
  submitToN8n: () => Promise<void>;
  sendVerificationCode: (phoneOverride?: string) => Promise<void>;
  verifyCode: (code: string) => Promise<boolean>;
  requestRevision: () => void;

  // Image upload & generation (new deferred flow)
  generationStatus: GenerationStatus;
  generationError: string | null;
  uploadImagesToS3: () => Promise<void>;
  triggerGeneration: () => Promise<void>;
  retryGeneration: () => Promise<void>;

  // Progressive lead creation
  createLead: () => Promise<void>;
  updateLead: (data: Record<string, unknown>) => Promise<void>;
  checkDuplicate: (phone: string) => Promise<{ duplicate: boolean; companyName?: string }>;
  checkCredits: () => Promise<{ hasCredits: boolean }>;
  requestCallback: () => Promise<void>;

  // Generation results
  setGenerationResults: (results: {
    geminiAnalysis: WidgetGeminiAnalysis;
    generatedImage: string;
    blurredPreview: string;
  }) => void;

  // Session management
  resetSession: () => void;
  hasIncompleteSession: boolean;
  resumeSession: () => void;
  startFreshSession: () => void;

  // Analytics
  trackEvent: (event: WidgetAnalyticsEvent, metadata?: Record<string, unknown>) => void;
}

const WidgetContext = createContext<WidgetContextValue | null>(null);

const STORAGE_KEY = 'renovation_widget_session';
const RATE_LIMIT_KEY = 'renovation_widget_rate_limit';
const MAX_VERIFICATION_ATTEMPTS = 3;
const RATE_LIMIT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

// Use cryptographically secure session ID generation
function generateSessionId(): string {
  return generateSecureId('ws');
}

// Rate limiting for phone verification
interface RateLimitEntry {
  attempts: number;
  firstAttemptAt: number;
}

function checkRateLimit(phone: string): { allowed: boolean; remainingAttempts: number } {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const limits: Record<string, RateLimitEntry> = stored ? JSON.parse(stored) : {};

    const entry = limits[phone];
    const now = Date.now();

    if (!entry) {
      return { allowed: true, remainingAttempts: MAX_VERIFICATION_ATTEMPTS };
    }

    // Reset if window has passed
    if (now - entry.firstAttemptAt > RATE_LIMIT_WINDOW_MS) {
      return { allowed: true, remainingAttempts: MAX_VERIFICATION_ATTEMPTS };
    }

    const remaining = MAX_VERIFICATION_ATTEMPTS - entry.attempts;
    return { allowed: remaining > 0, remainingAttempts: Math.max(0, remaining) };
  } catch {
    return { allowed: true, remainingAttempts: MAX_VERIFICATION_ATTEMPTS };
  }
}

function recordVerificationAttempt(phone: string): void {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const limits: Record<string, RateLimitEntry> = stored ? JSON.parse(stored) : {};

    const now = Date.now();
    const entry = limits[phone];

    if (!entry || now - entry.firstAttemptAt > RATE_LIMIT_WINDOW_MS) {
      // Start new window
      limits[phone] = { attempts: 1, firstAttemptAt: now };
    } else {
      // Increment attempts in current window
      limits[phone] = { ...entry, attempts: entry.attempts + 1 };
    }

    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(limits));
  } catch {
    // Ignore storage errors
  }
}

function getInitialState(tenantId: string): WidgetSessionState {
  return {
    sessionId: null,
    tenantId,
    currentStep: 'room_select',
    roomType: null,
    projectTypes: [],
    uploadedImages: [],
    imageFiles: [],
    selections: {},
    projectTypeSelections: {},
    contact: {
      first_name: '',
      last_name: '',
      email: '',
    },
    stylePreference: null,
    additionalDetails: null,
    geminiAnalysis: null,
    generatedImage: null,
    blurredPreview: null,
    // quoteRange removed - pricing no longer shown
    phoneNumber: null,
    revisionCount: 0,
    leadId: null,
    hasCredits: null,
    startedAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
  };
}

interface WidgetProviderProps {
  tenantId: string;
  tenantConfig: WidgetContextValue['tenantConfig'];
  children: ReactNode;
}

export function WidgetProvider({ tenantId, tenantConfig, children }: WidgetProviderProps) {
  const [state, setState] = useState<WidgetSessionState>(() => getInitialState(tenantId));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasIncompleteSession, setHasIncompleteSession] = useState(false);

  // Generation tracking (transient - not persisted to localStorage)
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus>('idle');
  const [generationError, setGenerationError] = useState<string | null>(null);
  const generationStartedRef = useRef(false);

  // Check for incomplete session on mount (with encrypted storage)
  useEffect(() => {
    const checkSession = async () => {
      try {
        const saved = localStorage.getItem(`${STORAGE_KEY}_${tenantId}`);
        if (saved) {
          // Try to decrypt (handles both encrypted and legacy unencrypted data)
          const parsed = await decryptFromStorage<WidgetSessionState>(saved);
          if (parsed && parsed.currentStep !== 'results' && parsed.roomType) {
            // Migrate legacy 'processing' step to 'preview_teaser'
            if ((parsed.currentStep as string) === 'processing') {
              parsed.currentStep = 'preview_teaser';
            }
            setHasIncompleteSession(true);
          }
        }
      } catch {
        // Ignore parse/decrypt errors
      }
    };
    checkSession();
  }, [tenantId]);

  // Save state to localStorage with encryption (for PII protection)
  useEffect(() => {
    const saveState = async () => {
      if (state.sessionId) {
        const toSave = {
          ...state,
          imageFiles: [], // Don't save File objects
          lastUpdated: new Date().toISOString(),
        };
        try {
          const encrypted = await encryptForStorage(toSave);
          localStorage.setItem(`${STORAGE_KEY}_${tenantId}`, encrypted);
        } catch {
          // Fallback: save non-sensitive data only if encryption fails
          const minimalData = {
            sessionId: state.sessionId,
            tenantId: state.tenantId,
            currentStep: state.currentStep,
            roomType: state.roomType,
            // Exclude PII: contact, phoneNumber, address
          };
          localStorage.setItem(`${STORAGE_KEY}_${tenantId}`, JSON.stringify(minimalData));
        }
      }
    };
    saveState();
  }, [state, tenantId]);

  // Navigation
  const currentStepIndex = STEP_ORDER.indexOf(state.currentStep);
  const canGoPrev = currentStepIndex > 0 && state.currentStep !== 'preview_teaser' && state.currentStep !== 'results';
  const canGoNext = currentStepIndex < STEP_ORDER.length - 1;

  // trackEvent defined early so other callbacks can reference it
  const trackEvent = useCallback((event: WidgetAnalyticsEvent, metadata?: Record<string, unknown>) => {
    if (!state.sessionId) return;
    if (import.meta.env.DEV) {
      console.debug('[Widget Analytics]', event, { sessionId: state.sessionId, ...metadata });
    }
  }, [state.sessionId]);

  const goToStep = useCallback((step: WidgetStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const nextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setState((prev) => ({ ...prev, currentStep: STEP_ORDER[nextIndex] }));
    }
  }, [currentStepIndex]);

  const prevStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setState((prev) => ({ ...prev, currentStep: STEP_ORDER[prevIndex] }));
    }
  }, [currentStepIndex]);

  // Actions
  const setRoomType = useCallback((type: RoomType) => {
    setState((prev) => ({
      ...prev,
      roomType: type,
      projectTypes: [], // Reset project types when room type changes
      projectTypeSelections: {}, // Reset project type selections
      sessionId: prev.sessionId || generateSessionId(),
    }));
  }, []);

  const setProjectTypes = useCallback((types: string[]) => {
    setState((prev) => ({ ...prev, projectTypes: types }));
  }, []);

  /** @deprecated Use setProjectTypes instead */
  const setConversionType = useCallback((type: string | undefined) => {
    // Convert single conversion type to array for backward compatibility
    setState((prev) => ({ ...prev, projectTypes: type ? [type] : [] }));
  }, []);

  const addImageFile = useCallback((file: File) => {
    setState((prev) => {
      if (prev.imageFiles.length >= 3) return prev;
      return { ...prev, imageFiles: [...prev.imageFiles, file] };
    });
  }, []);

  const removeImageFile = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      imageFiles: prev.imageFiles.filter((_, i) => i !== index),
    }));
  }, []);

  const setUploadedImages = useCallback((paths: string[]) => {
    setState((prev) => ({ ...prev, uploadedImages: paths }));
  }, []);

  const updateSelections = useCallback((selections: Partial<RenovationSelections>) => {
    setState((prev) => ({
      ...prev,
      selections: { ...prev.selections, ...selections },
    }));
  }, []);

  const updateProjectTypeSelections = useCallback((projectTypeId: string, selections: Partial<RenovationSelections>) => {
    setState((prev) => ({
      ...prev,
      projectTypeSelections: {
        ...prev.projectTypeSelections,
        [projectTypeId]: { ...prev.projectTypeSelections[projectTypeId], ...selections },
      },
    }));
  }, []);

  const setStylePreference = useCallback((style: string) => {
    setState((prev) => ({ ...prev, stylePreference: style }));
  }, []);

  const setAdditionalDetails = useCallback((details: string | undefined) => {
    setState((prev) => ({ ...prev, additionalDetails: details ?? null }));
  }, []);

  const updateContact = useCallback((contact: Partial<WidgetContact>) => {
    setState((prev) => ({
      ...prev,
      contact: { ...prev.contact, ...contact },
    }));
  }, []);

  const setPhoneNumber = useCallback((phone: string) => {
    setState((prev) => ({ ...prev, phoneNumber: phone }));
  }, []);

  // n8n integration - transitions to preview teaser (S3 upload happens there)
  const submitToN8n = useCallback(async () => {
    if (!state.sessionId || !state.roomType) {
      setError('Missing required data');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      goToStep('preview_teaser');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [state.sessionId, state.roomType, goToStep]);

  // Upload images to S3 using presigned URLs (called from PreviewTeaserStep)
  const uploadImagesToS3 = useCallback(async () => {
    if (state.uploadedImages.length > 0) {
      // Already uploaded (e.g., session resume)
      setGenerationStatus('idle');
      return;
    }

    setGenerationStatus('uploading');
    setGenerationError(null);

    const presignedEndpoint = import.meta.env.VITE_PRESIGNED_URL_ENDPOINT;
    if (!presignedEndpoint) {
      setGenerationStatus('error');
      setGenerationError('Upload service not configured. Please try again later.');
      return;
    }

    try {
      const uploadedPaths: string[] = [];

      for (let i = 0; i < state.imageFiles.length; i++) {
        const file = state.imageFiles[i];
        // Always use .jpg — images are converted to JPEG on upload
        const fileName = `${Date.now()}_${i}.jpg`;

        // Get presigned URL with retry
        const presignedResponse = await fetchWithRetry(
          presignedEndpoint,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tenantId: state.tenantId,
              sessionId: state.sessionId,
              fileName,
              fileType: file.type || 'image/jpeg',
            }),
          },
          { maxRetries: 2, timeoutMs: 10000, backoffMs: 1000 }
        );

        if (!presignedResponse.ok) {
          throw new Error(`Failed to get upload URL (HTTP ${presignedResponse.status})`);
        }

        const parsed = await safeJsonParse<{ data?: { uploadUrl: string; fileUrl: string }; uploadUrl?: string; fileUrl?: string }>(presignedResponse);
        if (!parsed.ok) {
          throw new Error(parsed.error);
        }

        const { uploadUrl, fileUrl } = parsed.data.data || parsed.data;
        if (!uploadUrl || !fileUrl) {
          throw new Error('Invalid upload URL response');
        }

        // Upload to S3 with retry
        const uploadResponse = await fetchWithRetry(
          uploadUrl,
          {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'image/jpeg' },
            body: file,
          },
          { maxRetries: 2, timeoutMs: 30000, backoffMs: 1000 }
        );

        if (!uploadResponse.ok) {
          throw new Error(`Image upload failed (HTTP ${uploadResponse.status})`);
        }

        uploadedPaths.push(fileUrl);
      }

      setUploadedImages(uploadedPaths);
      setGenerationStatus('idle');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload images';
      console.error('S3 upload error:', err);
      setGenerationStatus('error');
      setGenerationError(message);
    }
  }, [state.imageFiles, state.uploadedImages, state.tenantId, state.sessionId, setUploadedImages]);

  const setGenerationResults = useCallback((results: {
    geminiAnalysis: WidgetGeminiAnalysis;
    generatedImage: string;
    blurredPreview: string;
  }) => {
    setState((prev) => {
      return {
        ...prev,
        geminiAnalysis: results.geminiAnalysis,
        generatedImage: results.generatedImage,
        blurredPreview: results.blurredPreview,
      };
    });
  }, []);

  // Trigger n8n image generation (called from ProjectDetailsStep)
  const triggerGeneration = useCallback(async () => {
    // Prevent double-triggering
    if (generationStartedRef.current) return;
    // Skip if already have results (e.g., revision flow)
    if (state.generatedImage) {
      setGenerationStatus('complete');
      return;
    }

    generationStartedRef.current = true;
    setGenerationStatus('generating');
    setGenerationError(null);

    const webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL;
    if (!webhookUrl) {
      setGenerationStatus('error');
      setGenerationError('Generation service not configured.');
      generationStartedRef.current = false;
      return;
    }

    if (state.uploadedImages.length === 0) {
      setGenerationStatus('error');
      setGenerationError('No images uploaded. Please go back and upload photos.');
      generationStartedRef.current = false;
      return;
    }

    try {
      const payload = {
        tenantId: state.tenantId,
        sessionId: state.sessionId,
        roomType: state.roomType,
        projectTypes: state.projectTypes,
        projectTypeSelections: state.projectTypeSelections,
        conversionType: state.projectTypes.length > 0 ? state.projectTypes[0] : null,
        images: state.uploadedImages,
        selections: Object.keys(state.projectTypeSelections || {}).length > 0
          ? state.projectTypeSelections
          : state.selections,
        stylePreference: state.stylePreference,
        additionalDetails: state.additionalDetails,
        contact: state.contact,
        watermarkText: tenantConfig?.widget?.watermark_text || tenantConfig?.company_name || 'RenovationAI',
      };

      // n8n can take 30-90 seconds for generation
      const response = await fetchWithTimeout(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }, 120000); // 2 minute timeout

      if (!response.ok) {
        throw new Error(`Generation failed (HTTP ${response.status}: ${response.statusText})`);
      }

      const parsed = await safeJsonParse<{
        status?: string;
        message?: string;
        geminiAnalysis?: Partial<WidgetGeminiAnalysis>;
        generatedImage?: string;
        blurredPreview?: string;
      }>(response);

      if (!parsed.ok) {
        throw new Error(parsed.error);
      }

      const result = parsed.data;

      if (result.status === 'error') {
        throw new Error(result.message || 'Generation failed');
      }

      if (!result.generatedImage) {
        throw new Error('No image returned from generation service');
      }

      // Build analysis with sensible defaults
      const roomTypeElements: Record<string, string[]> = {
        kitchen: ['Cabinetry', 'Countertops', 'Appliances', 'Flooring'],
        bathroom: ['Vanity', 'Fixtures', 'Tile', 'Lighting'],
        basement: ['Foundation walls', 'Flooring', 'Ceiling', 'Windows'],
        living_room: ['Flooring', 'Walls', 'Windows', 'Lighting'],
        bedroom: ['Flooring', 'Walls', 'Closet', 'Windows'],
        outdoor: ['Decking', 'Railings', 'Landscaping'],
        garage: ['Flooring', 'Storage', 'Walls'],
        home_office: ['Desk area', 'Shelving', 'Lighting', 'Flooring'],
      };

      const defaultDetectedElements = state.roomType && roomTypeElements[state.roomType]
        ? roomTypeElements[state.roomType]
        : ['Room features analyzed'];

      const defaultComplexityFactors: string[] = [];
      if (state.selections?.flooring) defaultComplexityFactors.push('Flooring replacement');
      if (state.selections?.walls) defaultComplexityFactors.push('Wall treatment');
      if (state.selections?.cabinets) defaultComplexityFactors.push('Cabinet work');
      if (state.selections?.countertops) defaultComplexityFactors.push('Countertop installation');
      if (state.selections?.lighting) defaultComplexityFactors.push('Electrical work');

      const rawAnalysis = result.geminiAnalysis || {};
      const geminiAnalysis: WidgetGeminiAnalysis = {
        room_type: rawAnalysis.room_type || state.roomType || 'kitchen',
        room_size: rawAnalysis.room_size || 'medium',
        current_condition: rawAnalysis.current_condition || 'fair',
        complexity_factors: rawAnalysis.complexity_factors?.length
          ? rawAnalysis.complexity_factors
          : (defaultComplexityFactors.length > 0 ? defaultComplexityFactors : ['Standard renovation scope']),
        detected_elements: rawAnalysis.detected_elements?.length
          ? rawAnalysis.detected_elements
          : defaultDetectedElements,
        notes: rawAnalysis.notes || `${state.stylePreference || 'Modern'} style renovation with ${Object.keys(state.selections || {}).length} area(s) selected.`,
      };

      setGenerationResults({
        geminiAnalysis,
        generatedImage: result.generatedImage,
        blurredPreview: result.blurredPreview || result.generatedImage,
      });

      setGenerationStatus('complete');
      trackEvent('generation_completed', {
        room_size: geminiAnalysis.room_size,
        condition: geminiAnalysis.current_condition,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Generation failed. Please try again.';
      console.error('Generation error:', err);
      setGenerationStatus('error');
      setGenerationError(message);
      generationStartedRef.current = false; // Allow retry
    }
  }, [state, tenantConfig, setGenerationResults, trackEvent]);

  // Retry generation (called from ResultsStep on failure)
  const retryGeneration = useCallback(async () => {
    generationStartedRef.current = false;
    setGenerationStatus('idle');
    setGenerationError(null);
    await triggerGeneration();
  }, [triggerGeneration]);

  const sendVerificationCode = useCallback(async (phoneOverride?: string) => {
    // Use override to avoid stale closure on first click
    const phone = phoneOverride || state.phoneNumber;
    if (!phone || !state.sessionId) return;

    // Check client-side rate limiting first (backup for server-side)
    const rateCheck = checkRateLimit(phone);
    if (!rateCheck.allowed) {
      setError(`Too many attempts. Please try again in 5 minutes.`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Record this verification attempt for client-side rate limiting
      recordVerificationAttempt(phone);

      // Call Twilio Verify API to send SMS code
      const response = await fetch(`${API_BASE_URL}/verify/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone,
          tenantId: state.tenantId,
          sessionId: state.sessionId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 429) {
          setError(result.error || 'Too many attempts. Please try again later.');
          return;
        }
        throw new Error(result.error || 'Failed to send verification code');
      }

      // Success - transition to verify step
      goToStep('verify_code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [state.phoneNumber, state.sessionId, state.tenantId, goToStep]);

  const verifyCode = useCallback(async (code: string): Promise<boolean> => {
    if (!state.phoneNumber || !state.sessionId) {
      setError('Session error. Please start over.');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Validate code format
      if (code.length !== 6 || !/^\d{6}$/.test(code)) {
        setError('Please enter a valid 6-digit code');
        return false;
      }

      // Call Twilio Verify API to check code
      const response = await fetch(`${API_BASE_URL}/verify/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: state.phoneNumber,
          code: code,
          tenantId: state.tenantId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 429) {
          setError(result.error || 'Too many incorrect attempts. Please request a new code.');
          return false;
        }
        setError(result.error || 'Invalid verification code');
        return false;
      }

      if (result.data?.verified) {
        // Success - transition to project_details (address/timeline/notes)
        goToStep('project_details');
        return true;
      }

      setError('Verification failed. Please try again.');
      return false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [state.phoneNumber, state.sessionId, state.tenantId, goToStep]);

  const requestRevision = useCallback(() => {
    if (state.revisionCount >= 1) {
      setError('Maximum revisions reached. Book a call to explore more options.');
      return;
    }
    setState((prev) => ({
      ...prev,
      revisionCount: prev.revisionCount + 1,
      currentStep: 'selections',
    }));
  }, [state.revisionCount]);

  // Progressive lead creation - create lead with contact info
  const createLead = useCallback(async () => {
    if (!state.sessionId) {
      throw new Error('Missing session ID');
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        tenantId: state.tenantId,
        sessionId: state.sessionId,
        clientName: `${state.contact.first_name || ''} ${state.contact.last_name || ''}`.trim() || 'Anonymous',
        clientEmail: state.contact.email || undefined,
        roomType: state.roomType,
        selections: state.selections,
        projectTypes: state.projectTypes,
        projectTypeSelections: state.projectTypeSelections,
        uploadedImages: state.uploadedImages,
        stylePreference: state.stylePreference,
        sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      };

      const response = await fetch(`${API_BASE_URL}/widget/create-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create lead');
      }

      setState((prev) => ({ ...prev, leadId: result.data?.leadId }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create lead');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [state.sessionId, state.tenantId, state.contact, state.roomType, state.selections, state.projectTypes, state.projectTypeSelections, state.uploadedImages, state.stylePreference]);

  // Update lead with additional data
  const updateLead = useCallback(async (data: Record<string, unknown>) => {
    if (!state.leadId) {
      console.warn('No leadId to update');
      return;
    }

    try {
      const payload = {
        leadId: state.leadId,
        tenantId: state.tenantId,
        ...data,
      };

      const response = await fetch(`${API_BASE_URL}/widget/update-lead`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update lead');
      }
    } catch (err) {
      console.error('Lead update error:', err);
      throw err;
    }
  }, [state.leadId, state.tenantId]);

  // Check for duplicate phone number
  const checkDuplicate = useCallback(async (phone: string): Promise<{ duplicate: boolean; companyName?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/widget/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone,
          tenantId: state.tenantId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to check duplicate');
      }

      return {
        duplicate: result.data?.duplicate || false,
        companyName: result.data?.companyName,
      };
    } catch (err) {
      console.error('Duplicate check error:', err);
      return { duplicate: false };
    }
  }, [state.tenantId]);

  // Check if tenant has credits available
  const checkCredits = useCallback(async (): Promise<{ hasCredits: boolean }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/widget/check-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantId: state.tenantId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to check credits');
      }

      const hasCredits = result.data?.hasCredits || false;
      setState((prev) => ({ ...prev, hasCredits }));

      return { hasCredits };
    } catch (err) {
      console.error('Credits check error:', err);
      return { hasCredits: false };
    }
  }, [state.tenantId]);

  // Request callback from contractor
  const requestCallback = useCallback(async () => {
    if (!state.leadId) {
      throw new Error('No lead ID');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/widget/request-callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          leadId: state.leadId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to request callback');
      }
    } catch (err) {
      console.error('Callback request error:', err);
      throw err;
    }
  }, [state.leadId]);

  // Session management
  const resetSession = useCallback(() => {
    localStorage.removeItem(`${STORAGE_KEY}_${tenantId}`);
    setState(getInitialState(tenantId));
    setHasIncompleteSession(false);
  }, [tenantId]);

  const resumeSession = useCallback(async () => {
    try {
      const saved = localStorage.getItem(`${STORAGE_KEY}_${tenantId}`);
      if (saved) {
        // Try to decrypt (handles both encrypted and legacy unencrypted data)
        const parsed = await decryptFromStorage<WidgetSessionState>(saved);
        if (parsed) {
          // Migrate legacy 'processing' step to 'preview_teaser'
          if ((parsed.currentStep as string) === 'processing') {
            parsed.currentStep = 'preview_teaser';
          }
          setState({ ...parsed, imageFiles: [] });
        } else {
          // If decryption failed, try legacy JSON parse
          try {
            const legacy = JSON.parse(saved) as WidgetSessionState;
            if ((legacy.currentStep as string) === 'processing') {
              legacy.currentStep = 'preview_teaser';
            }
            setState({ ...legacy, imageFiles: [] });
          } catch {
            resetSession();
          }
        }
      }
    } catch {
      // On error, start fresh
      resetSession();
    }
    setHasIncompleteSession(false);
  }, [tenantId, resetSession]);

  const startFreshSession = useCallback(() => {
    resetSession();
    setHasIncompleteSession(false);
  }, [resetSession]);

  // Set Sentry context when session is created
  useEffect(() => {
    if (state.sessionId && tenantId) {
      setSentryWidgetSession(state.sessionId, tenantId);
    }
  }, [state.sessionId, tenantId]);

  // Analytics tracking - stub for future PostHog integration
  // Firebase Analytics removed in FR-6 migration cleanup
  const value: WidgetContextValue = {
    state,
    tenantConfig,
    isLoading,
    error,
    currentStep: state.currentStep,
    canGoNext,
    canGoPrev,
    goToStep,
    nextStep,
    prevStep,
    setRoomType,
    setProjectTypes,
    setConversionType,
    addImageFile,
    removeImageFile,
    setUploadedImages,
    updateSelections,
    updateProjectTypeSelections,
    setStylePreference,
    setAdditionalDetails,
    updateContact,
    setPhoneNumber,
    submitToN8n,
    sendVerificationCode,
    verifyCode,
    requestRevision,
    generationStatus,
    generationError,
    uploadImagesToS3,
    triggerGeneration,
    retryGeneration,
    createLead,
    updateLead,
    checkDuplicate,
    checkCredits,
    requestCallback,
    setGenerationResults,
    resetSession,
    hasIncompleteSession,
    resumeSession,
    startFreshSession,
    trackEvent,
  };

  return <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>;
}

export function useWidget() {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error('useWidget must be used within a WidgetProvider');
  }
  return context;
}
